import { useEffect, useMemo, useState } from 'react'

import { buildDeletedLotRowMatcher, loadDeletedLots, type DeletedLotRef } from '../utils/deletedLots'
import { lotApi } from '../utils/lotApi'

type FarmLotRef = { id: string; lotKey?: string; productionUnitId?: string; createdAt?: string }

/**
 * Findings #200: rows of a deleted lot are left out of current views (Biomasa,
 * Panel). Loads the deleted lots of each farm and the lots on this device, and
 * returns `isDeletedLotRow(row, fallbackUnitId?)`. Until both are known, or
 * offline before the first load, nothing is treated as deleted.
 */
export const useDeletedLotRowMatcher = (farmIds: ReadonlyArray<string>) => {
  const farmKey = useMemo(
    () => Array.from(new Set(farmIds.map((id) => String(id ?? '').trim()).filter(Boolean))).sort().join('|'),
    [farmIds]
  )
  const [deletedLots, setDeletedLots] = useState<DeletedLotRef[]>([])
  const [farmLots, setFarmLots] = useState<FarmLotRef[]>([])

  useEffect(() => {
    const ids = farmKey ? farmKey.split('|') : []
    if (ids.length === 0) {
      setDeletedLots([])
      setFarmLots([])
      return
    }
    let cancelled = false
    void Promise.all([
      Promise.all(ids.map((id) => loadDeletedLots(id))),
      lotApi.list(undefined, { preferLocal: true }).catch(() => [])
    ]).then(([deletedByFarm, lots]) => {
      if (cancelled) return
      setDeletedLots(deletedByFarm.flatMap((list) => list ?? []))
      setFarmLots((lots ?? []).map((lot) => ({
        id: String(lot.id),
        lotKey: String(lot.lotKey ?? ''),
        ...(lot.productionUnitId ? { productionUnitId: String(lot.productionUnitId) } : {}),
        ...(lot.createdAt ? { createdAt: String(lot.createdAt) } : {})
      })))
    })
    return () => {
      cancelled = true
    }
  }, [farmKey])

  return useMemo(() => buildDeletedLotRowMatcher(deletedLots, farmLots), [deletedLots, farmLots])
}
